import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { Button } from "antd";
import { ArrowLeftOutlined, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

export default function TimerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState("");
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [percentage, setPercentage] = useState(0);


useEffect(() => {
  let interval;

  const fetchActiveSession = async () => {
    const todayStart = dayjs().startOf("day").toDate();
    const q = query(
      collection(db, "visiteur"),
      where("table", "==", Number(id)),
      where("sortie", "==", false),
      where("startTime", ">=", todayStart)
    );

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const visitor = snapshot.docs[0].data();
      const startTime = dayjs(visitor.startTime.toDate());
      const endTime = startTime.add(visitor.hours, "hour");
      const totalDuration = endTime.diff(startTime);

      interval = setInterval(() => {
        const now = dayjs();
        const diff = endTime.diff(now);

        if (diff > 0) {
          const d = dayjs.duration(diff);
          const h = String(d.hours()).padStart(2, "0");
          const m = String(d.minutes()).padStart(2, "0");
          const s = String(d.seconds()).padStart(2, "0");
          setTimeLeft(`${h}:${m}:${s}`);
          setValid(true);

          const percentRemaining = Math.max(
            0,
            Math.min(100, (diff / totalDuration) * 100)
          );
          setPercentage(percentRemaining);
        } else {
          setTimeLeft("00:00:00");
          setValid(false);
          setPercentage(0);
          clearInterval(interval);
        }
      }, 1000);
    }

    setLoading(false);
  };

  fetchActiveSession();

  return () => clearInterval(interval);
}, [id]);

  // Determine color based on time remaining
  const getStatusColor = () => {
    if (!valid) return "#6b7280";
    if (percentage > 50) return "#10b981"; // Green
    if (percentage > 20) return "#f59e0b"; // Orange
    return "#ef4444"; // Red
  };

  const getStatusText = () => {
    if (!valid) return "Session terminée";
    if (percentage > 50) return "Temps restant";
    if (percentage > 20) return "Attention au temps";
    return "Temps presque écoulé";
  };

  const statusColor = getStatusColor();

  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${statusColor} 0%, ${statusColor}dd 100%)`,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated background circles */}
      <div
        style={{
          position: "absolute",
          top: "-10%",
          right: "-10%",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)",
          filter: "blur(60px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-15%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)",
          filter: "blur(80px)",
        }}
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "1rem",
          paddingTop: "1.5rem",
          position: "relative",
          zIndex: 10,
        }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: "1.25rem" }} />}
          onClick={() => navigate(`/table/${id}`)}
          style={{
            color: "#fff",
            padding: "0.5rem",
          }}
        />
        <h1
          style={{
            color: "#fff",
            fontSize: "1.25rem",
            fontWeight: "700",
            margin: 0,
            marginLeft: "0.5rem",
          }}
        >
          Table {id}
        </h1>
      </div>

      {/* Main Content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "2rem 1.5rem",
          position: "relative",
          zIndex: 10,
        }}
      >
        {loading ? (
          <div
            style={{
              color: "#fff",
              fontSize: "1.5rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            <ClockCircleOutlined
              style={{ fontSize: "3rem" }}
              spin
            />
            <span>Chargement...</span>
          </div>
        ) : (
          <>
            {/* Status badge */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                borderRadius: "50px",
                padding: "0.75rem 1.5rem",
                marginBottom: "2rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  animation: valid ? "pulse 2s infinite" : "none",
                }}
              />
              <span
                style={{
                  color: "#fff",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {getStatusText()}
              </span>
            </div>

            {/* Timer Display Card */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "32px",
                padding: "3rem 2rem",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                marginBottom: "2rem",
                position: "relative",
                overflow: "hidden",
                minWidth: "320px",
                maxWidth: "400px",
                width: "90%",
              }}
            >
              {/* Progress bar background */}
              {valid && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "6px",
                    backgroundColor: "#e5e7eb",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      backgroundColor: statusColor,
                      width: `${percentage}%`,
                      transition: "width 1s linear",
                    }}
                  />
                </div>
              )}

              {/* Timer digits */}
              <div
                style={{
                  fontSize: "4rem",
                  fontWeight: "700",
                  color: statusColor,
                  letterSpacing: "0.05em",
                  textAlign: "center",
                  fontVariantNumeric: "tabular-nums",
                  fontFamily: "'SF Mono', 'Monaco', 'Courier New', monospace",
                  lineHeight: 1,
                }}
              >
                {timeLeft || "00:00:00"}
              </div>

              {/* Time labels */}
              {valid && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "2rem",
                    marginTop: "1.5rem",
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    fontWeight: "600",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  <span>Heures</span>
                  <span>Minutes</span>
                  <span>Secondes</span>
                </div>
              )}
            </div>

            {/* Status message */}
            {!valid && (
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "16px",
                  padding: "1.5rem",
                  maxWidth: "400px",
                  width: "90%",
                }}
              >
                <p
                  style={{
                    color: "#fff",
                    fontSize: "1rem",
                    margin: 0,
                    textAlign: "center",
                    lineHeight: 1.6,
                  }}
                >
                  Aucune session active aujourd'hui
                </p>
              </div>
            )}

            {/* Additional info for active sessions */}
            {valid && (
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "16px",
                  padding: "1rem 1.5rem",
                  maxWidth: "400px",
                  width: "90%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                <ClockCircleOutlined style={{ color: "#fff", fontSize: "1.25rem" }} />
                <span
                  style={{
                    color: "#fff",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  {Math.round(percentage)}% du temps restant
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
}